// frontend/src/pages/CreatorDirectory.jsx
// ✅ REPAIRED: Relies on backend for accurate royalty calculations
// ✅ UPDATED: Removed USD displays to keep app Pi-native

import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { getIdentityHeaders, waitForIdentity } from "../lib/identity";
import { usePiPrice } from "../contexts/PriceContext";
import EarningsAnalytics from "../components/EarningsAnalytics.jsx";
import PaymentHistory from "../components/PaymentHistory.jsx";
import {
  API_BASE,
  fmtPi,
  toast
} from "../lib/api";

const api = (path) => API_BASE ? `${API_BASE}${path}` : path;

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralText: "#111827",
  neutralBorder: "#E5E7EB",
  shadow: "0 10px 24px rgba(17,24,39,.08)",
};

const USD_PER_CREDIT = 0.17;

export default function CreatorDirectory() {
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // Backend royalty data
  const [summaryLoading, setSummaryLoading] = React.useState(true);
  const [availablePi, setAvailablePi] = React.useState(0);
  const [pendingPi, setPendingPi] = React.useState(0);
  const [totalPi, setTotalPi] = React.useState(0);
  const [exchangedCredits, setExchangedCredits] = React.useState(0);

  // Stats data (for display only, not calculation)
  const [totalUses, setTotalUses] = React.useState(0);
  const [stylesCount, setStylesCount] = React.useState(0);

  // OKX live pricing
  const { piPrice, piToUsd, loading: priceLoading, connected } = usePiPrice();

  // Collapsible sections
  const [earningsOpen, setEarningsOpen] = React.useState(() => {
    try { return localStorage.getItem("cd_earnings_open") !== "0"; } catch { return true; }
  });
  const [paymentsOpen, setPaymentsOpen] = React.useState(() => {
    try { return localStorage.getItem("cd_payments_open") === "1"; } catch { return false; }
  });
  const [statsOpen, setStatsOpen] = React.useState(() => {
    try { return localStorage.getItem("cd_stats_open") !== "0"; } catch { return true; }
  });

  // Fetch royalty summary
  const fetchRoyaltySummary = React.useCallback(async (forceRecalc = false) => {
    try {
      setSummaryLoading(true);
      
      const url = forceRecalc 
        ? api("/api/creator/royalties/summary?recalculate=true")
        : api("/api/creator/royalties/summary");
        
      const res = await fetch(url, {
        headers: getIdentityHeaders(),
      });
      
      if (res.ok) {
        const data = await res.json();
        setAvailablePi(Number(data.availablePi) || 0);
        setPendingPi(Number(data.pendingPi) || 0);
        setTotalPi(Number(data.totalPi) || 0);
        setExchangedCredits(Number(data.exchangedCredits) || 0);
      } else {
        console.warn("Failed to load royalty summary:", res.status);
      }
    } catch (e) {
      console.warn("Failed to load royalty summary:", e);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  // Fetch stats (for display)
  const fetchStats = React.useCallback(async () => {
    try {
      setLoading(true);
      await waitForIdentity();

      // Fetch style breakdown for stats display
      const res = await fetch(api("/api/creator/royalties/by-style?period=all"), {
        headers: getIdentityHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        
        // Calculate total uses from all styles
        const uses = items.reduce((sum, item) => sum + (item.uses || 0), 0);
        setTotalUses(uses);
        setStylesCount(items.length);
      }
    } catch (e) {
      console.error("Failed to load stats:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load
  React.useEffect(() => {
    fetchRoyaltySummary();
    fetchStats();
  }, [fetchRoyaltySummary, fetchStats]);

  // Refresh function
  const refreshAllData = React.useCallback(async () => {
    setRefreshing(true);
    
    try {
      await Promise.all([
        fetchRoyaltySummary(true), // Force recalculation
        fetchStats(),
      ]);
      
      toast("✅ Data refreshed!");
    } catch (e) {
      console.error("Refresh failed:", e);
      toast("❌ Refresh failed: " + (e.message || "Unknown error"));
    } finally {
      setRefreshing(false);
    }
  }, [fetchRoyaltySummary, fetchStats]);

  // Calculate dynamic exchange rate
  const creditsPerPi = piPrice ? piPrice / USD_PER_CREDIT : 0;
  const creditsAvailable = Math.floor(availablePi * creditsPerPi);

  function toggleEarnings() {
    const next = !earningsOpen;
    setEarningsOpen(next);
    try { localStorage.setItem("cd_earnings_open", next ? "1" : "0"); } catch {}
  }

  function togglePayments() {
    const next = !paymentsOpen;
    setPaymentsOpen(next);
    try { localStorage.setItem("cd_payments_open", next ? "1" : "0"); } catch {}
  }

  function toggleStats() {
    const next = !statsOpen;
    setStatsOpen(next);
    try { localStorage.setItem("cd_stats_open", next ? "1" : "0"); } catch {}
  }

  async function requestWithdraw() {
    // Test-claim flow: the backend pays a FIXED 1π, once per user.
    // The body is ignored server-side; never fake balance changes locally.
    try {
      const res = await fetch(api("/api/creator/payouts/request"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getIdentityHeaders()
        },
        body: JSON.stringify({}),
      });

      const data = await res.json();

      if (res.ok) {
        toast(`✅ 1 π sent to your wallet! Tx: ${String(data?.txid || "").slice(0, 10)}…`);
        // Balance is unchanged by the test claim — refresh from the backend
        // so the UI always reflects server truth.
        await fetchRoyaltySummary();
      } else if (res.status === 409) {
        toast("You've already claimed your payout.");
      } else if (res.status === 503) {
        toast("Withdrawals aren't enabled yet — coming with mainnet approval.");
      } else {
        toast(data?.error || "Withdrawal request failed");
      }
    } catch (e) {
      toast("Withdrawal endpoint not available yet.");
    }
  }

  async function exchangeForCredits() {
  const amountPi = Math.floor(availablePi * 1e6) / 1e6;
  if (!(amountPi > 0)) {
    toast("No royalties available to exchange yet.");
    return;
  }

  if (!piPrice) {
    toast("Price data not available yet. Please wait...");
    return;
  }

  const creditsToReceive = Math.floor(amountPi * creditsPerPi);

  if (creditsToReceive < 1) {
    toast("Not enough royalties to exchange for at least 1 credit.");
    return;
  }

  try {
    const res = await fetch(api("/api/creator/royalties/exchange"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getIdentityHeaders()
      },
      body: JSON.stringify({ 
        amountPi,
        creditsToReceive,
        rateUsed: creditsPerPi,
        usdPerCredit: USD_PER_CREDIT,
        piPriceUsed: piPrice
      }),
    });

    const data = await res.json();
    
    if (res.ok) {
      toast(`✅ Exchanged ${fmtPi(amountPi)} for ${creditsToReceive} credits!`);
      
      window.dispatchEvent(new Event('credits:refresh'));
      await fetchRoyaltySummary();
      
    } else {
      toast(`❌ Exchange failed: ${data?.error || "Unknown error"}`);
      console.error("Exchange failed:", data);
    }
  } catch (e) {
    toast(`❌ Exchange error: ${e.message || "Network error"}`);
    console.error("Exchange exception:", e);
  }
}

  return (
    <Screen>
      <StyleTag />

      {/* Hero */}
      <section className="cd-hero">
        <h1 
          className="h1"
          style={{
            background: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Your Economy
        </h1>
        <div className="ms-hero__subtitle-wrapper">
          <p className="ms-hero__subtitle">
            Track earnings • Exchange Pi for credits • Withdraw to wallet
          </p>
          <p className="ms-hero__tips">
            💡 Toggle View buttons for detailed breakdowns & Refresh to update data
          </p>
        </div>
      </section>

      {/* Nav */}
      <section className="cd-local-nav">
        <Link to="/profile" className="btn">← Profile</Link>
        <button 
          className="btn" 
          onClick={refreshAllData} 
          disabled={refreshing}
          title="Refresh all data"
        >
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
        <button className="btn" onClick={toggleStats}>
          {statsOpen ? "Hide" : "View"} All-times
        </button>
        <button className="btn" onClick={toggleEarnings}>
          {earningsOpen ? "Hide" : "View"} earnings analytics
        </button>
        <button className="btn" onClick={togglePayments}>
          {paymentsOpen ? "Hide" : "View"} payout history
        </button>
      </section>

      {/* KPIs - collapsible, ✅ removed usdValue */}
      {statsOpen && (
        <section className="cd-kpis">
          <Kpi label="All-time uses" value={loading ? "…" : totalUses} />
          <Kpi 
            label="All-time royalties" 
            value={summaryLoading ? "…" : fmtPi(totalPi)}
          />
        </section>
      )}

      {/* Royalty Breakdown */}
      <section className="cd-royalty-breakdown">
        <div className="cd-card">
          {/* Live Rate Indicator */}
          <div className="live-rate-section">
            <div className="live-rate-badge">
              <span className="live-rate-label">Live rate:</span>
              <span className="live-rate-value">
                {priceLoading ? "..." : `1 π = $${piPrice?.toFixed(4)}`}
              </span>
              <span className={`live-indicator ${connected ? 'connected' : 'disconnected'}`}>
                ●
              </span>
            </div>
          </div>

          <div className="cd-breakdown-grid">
            {/* ✅ Removed breakdown-usd */}
            <div className="breakdown-item">
              <div className="breakdown-label">Available to withdraw</div>
              <div className="breakdown-value">{summaryLoading ? "…" : fmtPi(availablePi)}</div>
            </div>
            <div className="breakdown-item">
              <div className="breakdown-label">Exchange for credits</div>
              <div className="breakdown-value highlight">
                {summaryLoading || priceLoading ? "…" : creditsAvailable}
              </div>
              <div className="breakdown-hint">
                {availablePi > 0 && piPrice 
                  ? `${fmtPi(availablePi)} ≈ ${creditsAvailable} credits` 
                  : piPrice 
                    ? `Rate: 1 π ≈ ${creditsPerPi.toFixed(2)} credits`
                    : "Loading rate..."}
              </div>
            </div>
            {/* ✅ Removed breakdown-usd */}
            <div className="breakdown-item">
              <div className="breakdown-label">All-time royalties</div>
              <div className="breakdown-value muted">
                {summaryLoading ? "…" : fmtPi(totalPi)}
              </div>
            </div>
          </div>
          
          {/* Dual Buttons */}
          <div className="action-buttons">
            <button
              className="btn btn-secondary action-btn"
              disabled={summaryLoading || !(availablePi > 0)}
              onClick={requestWithdraw}
              title="Royalties deposit to your mainnet wallet"
            >
              Withdraw (π) - Coming Soon
            </button>
            <button
              className="btn btn-primary action-btn"
              disabled={summaryLoading || priceLoading || !(availablePi > 0) || creditsAvailable < 1}
              onClick={exchangeForCredits}
              title={availablePi > 0 && creditsAvailable >= 1 ? `Exchange for ${creditsAvailable} credits` : "Not enough to exchange"}
            >
              Exchange for credits
            </button>
          </div>
          
          <div className="withdraw-note">
            Withdraw as π to your wallet, or exchange for credits to use on Artibytes.
            {piPrice && (
              <div style={{ marginTop: '4px', fontSize: '12px', opacity: 0.8 }}>
                Current rate: 1 π ≈ {creditsPerPi.toFixed(2)} credits 
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Earnings Analytics - collapsible */}
      {earningsOpen && (
        <EarningsAnalytics />
      )}

      {/* Payment History - collapsible */}
      {paymentsOpen && (
        <section className="cd-analytics-section">
          <div className="cd-card">
            <PaymentHistory />
          </div>
        </section>
      )}

    </Screen>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="kpi">
      <div className="kpi__value">{value}</div>
      <div className="kpi__label">{label}</div>
    </div>
  );
}

function StyleTag() {
  return (
    <style>{`
.cd-hero{
  padding:18px; border:1px solid ${brand.purpleBorder}; border-radius:16px;
  background: linear-gradient(180deg, ${brand.purpleTint}, #fff);
  margin-bottom:18px;
}

.ms-hero__subtitle-wrapper {
  margin-top: 12px;
}

.ms-hero__subtitle{ 
  color: #6B7280; 
  font-size: 15px;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.ms-hero__tips {
  color: #9CA3AF;
  font-size: 13px;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .ms-hero__subtitle {
    font-size: 14px;
  }
  
  .ms-hero__tips {
    font-size: 12px;
  }
}

.cd-local-nav{ display:flex; gap:10px; margin-bottom:18px; flex-wrap:wrap; }

@media (max-width: 640px){
  .cd-local-nav{
    gap:6px;
  }
  
  .cd-local-nav .btn{
    font-size:13px;
    height:38px;
    padding:0 12px;
  }
}

.cd-kpis{
  display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-bottom:18px;
}

@media (max-width: 600px){ 
  .cd-kpis{ 
    gap:8px;
  }
  .kpi{
    padding:10px 8px;
  }
  .kpi__value{ 
    font-size:20px;
  }
  .kpi__label{ 
    font-size:11px;
  }
}

.kpi{
  background:#fff; border:1px solid ${brand.neutralBorder}; border-radius:14px;
  padding:14px; text-align:center; box-shadow:${brand.shadow};
}
.kpi__value{ font-size:26px; font-weight:800; color:${brand.neutralText}; }
.kpi__label{ color:${brand.neutralText}; opacity:.7; font-size:14px; margin-top:4px; }

.cd-royalty-breakdown{ margin-bottom:18px; }

.live-rate-section{
  background: linear-gradient(135deg, ${brand.purpleTint} 0%, #FAFAFF 100%);
  border: 1px solid ${brand.purpleBorder};
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.live-rate-badge{
  display: flex;
  align-items: center;
  gap: 8px;
}

.live-rate-label{
  font-size: 14px;
  font-weight: 700;
  color: #6B7280;
}

.live-rate-value{
  font-size: 16px;
  font-weight: 900;
  color: ${brand.neutralText};
  background: white;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${brand.purpleBorder};
}

.live-indicator{
  font-size: 12px;
  animation: pulse 2s ease-in-out infinite;
}

.live-indicator.connected{
  color: #10B981;
}

.live-indicator.disconnected{
  color: #EF4444;
  animation: none;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@media (max-width: 600px) {
  .live-rate-section{
    padding: 10px 12px;
  }
  
  .live-rate-badge{
    width: 100%;
    justify-content: space-between;
  }
  
  .live-rate-label{
    font-size: 12px;
  }
  
  .live-rate-value{
    font-size: 14px;
    padding: 4px 10px;
  }
}

.cd-breakdown-grid{
  display:grid; 
  grid-template-columns:repeat(3,1fr); 
  gap:12px; 
  margin-bottom:16px;
}

@media (max-width: 900px){ 
  .cd-breakdown-grid{ 
    grid-template-columns:repeat(3,1fr);
    gap:8px;
  }
  
  .breakdown-item{
    padding: 12px 8px;
    background: #FAFAFA;
    border-radius: 10px;
    border: 1px solid ${brand.purpleBorder};
  }
  
  .breakdown-label{ 
    font-size:11px;
    margin-bottom:4px;
  }
  
  .breakdown-value{ 
    font-size:18px;
  }
  
  .breakdown-hint{
    font-size:10px;
    margin-top:2px;
  }
}

.breakdown-item{ text-align:center; }
.breakdown-label{ color:#6B7280; font-size:14px; margin-bottom:6px; font-weight:700; }
.breakdown-value{ font-size:22px; font-weight:900; color:${brand.neutralText}; }
.breakdown-value.muted{ opacity:.85; }
.breakdown-value.highlight{ color:${brand.purple}; }
.breakdown-hint{
  font-size:12px;
  color:#9CA3AF;
  margin-top:4px;
  font-weight:600;
}

.action-buttons{
  display:grid;
  grid-template-columns:repeat(2, 1fr);
  gap:12px;
  margin-bottom:12px;
}

@media (max-width: 640px){
  .action-buttons{
    grid-template-columns:1fr;
    gap:8px;
  }
  
  .action-btn{
    height:44px;
    font-size:14px;
  }
}

.action-btn{
  height:48px;
  font-size:15px;
  font-weight:800;
}

.withdraw-note{ 
  text-align:center; 
  color:#6B7280; 
  font-size:13px; 
  line-height:1.5;
}

@media (max-width: 640px){
  .withdraw-note{
    font-size:11px;
  }
}

.cd-analytics-section{ margin-bottom:18px; }

.cd-card{
  background:#fff; border:1px solid ${brand.neutralBorder}; border-radius:16px;
  padding:18px; box-shadow:${brand.shadow};
}

.muted{ color:#6B7280; }

.btn{
  height:42px; padding:0 16px; border-radius:12px;
  border:1px solid ${brand.purpleBorder}; 
  background: ${brand.purpleTint};
  color: ${brand.neutralText};
  font-weight:700; cursor:pointer; transition: all 0.2s;
  text-decoration:none; display:inline-flex; align-items:center; justify-content:center;
}
.btn:hover{
  background: #E9DEFF;
  border-color: ${brand.purple};
}
.btn-primary{ background:${brand.purple}; color:#fff; border-color:${brand.purple}; }
.btn-primary:hover{ background:${brand.purpleDark}; }
.btn-secondary{
  background:#fff;
  color:${brand.purple};
  border:1.5px solid ${brand.purpleBorder};
  font-weight:800;
}
.btn-secondary:hover{
  background:${brand.purpleTint};
  border-color:${brand.purple};
}
.btn:disabled{ opacity:.6; cursor:not-allowed; }

.link{ color:${brand.purple}; font-weight:700; text-decoration:none; }
.link:hover{ text-decoration:underline; }

.h1{ font-size:32px; font-weight:900; margin:0; }
.h2{ font-size:20px; font-weight:800; margin:0; }
    `}</style>
  );
}